// 測定セッション → ファームセットへの実績差分取り込み（§14）。純関数（保存は呼び出し側）。
//
// ウォーターマーク差分方式: 各カウンタが (importedRuns, importedCount) を持ち、
// ΔN = session.runs - importedRuns / Δk = count - importedCount を必ず「対」で取り込む。
// 二重取り込みは Δ=0 になって自然に防止され、取り込み後の継続測定→再取り込みは差分のみ、
// 行ごとの選択取り込みや途中追加カウンタ（観測窓が途中から）でも N と k の対応が壊れない。
import { resolveRate } from '../core/rate-model';
import { newId } from '../store/persist';
import { isGroupDrop } from '../store/schema';
import type { DropLine, FarmSet, GroupDrop, RateMode } from '../store/schema';
import type { MeasureDropCounter, MeasureSession } from '../store/measure-schema';
import { effectiveElapsedMs } from './measure';

export type ImportLineKind =
  | 'empirical-add' // 実績系の行: N += ΔN, k += Δk
  | 'direct-convert' // 直接λの行: 実績+予測へ変換（現在のλを事前分布に。情報を捨てない）
  | 'group-add' // グループ: 全メンバー一括で実績化・加算（重みの尺度を λ̂ に揃える）
  | 'new-line' // adhoc: セットへ新規実績行を作成
  | 'orphan' // 対応行がセットから削除済み。選択すると new-line として追加
  | 'blocked'; // 取り込み不可（グループ削除済み・メンバーのカウンタ不足）

export interface ImportPlanLine {
  /** 選択キー。通常行/adhoc = counterId、グループ = `group:<groupId>`。 */
  key: string;
  kind: ImportLineKind;
  name: string;
  /** 表示用差分（グループは メンバー最大ΔN / Δk 合計）。 */
  deltaN: number;
  deltaK: number;
  selectable: boolean;
  note: string;
}

export interface ImportPlan {
  lines: ImportPlanLine[];
  /** 周回時間 t の更新提案（実測平均・全期間）。差が無い/実測不能なら null。 */
  timeProposal: { current: number; measured: number } | null;
  /** 取り込める差分が何も無い。 */
  empty: boolean;
}

export interface ImportChoices {
  /** 取り込む plan line の key 集合。 */
  selected: Set<string>;
  /** t を実測平均へ更新するか。 */
  applyTime: boolean;
}

export interface ImportResult {
  /** 加算・変換した既存行（グループは1件と数える）。 */
  updatedLines: number;
  /** 新規作成したドロップ行。 */
  addedLines: number;
  totalDeltaK: number;
  maxDeltaN: number;
  timeApplied: boolean;
}

function deltaN(session: MeasureSession, c: MeasureDropCounter): number {
  return Math.max(0, session.runs - c.importedRuns);
}

function deltaK(c: MeasureDropCounter): number {
  return Math.max(0, c.count - c.importedCount);
}

function findLine(set: FarmSet, lineId: string): DropLine | null {
  for (const entry of set.dropLines) {
    if (!isGroupDrop(entry) && entry.id === lineId) return entry;
  }
  return null;
}

function findGroup(set: FarmSet, groupId: string): GroupDrop | null {
  for (const entry of set.dropLines) {
    if (isGroupDrop(entry) && entry.id === groupId) return entry;
  }
  return null;
}

export function planImport(session: MeasureSession, set: FarmSet, now: number): ImportPlan {
  const lines: ImportPlanLine[] = [];

  // 通常カウンタ（set-line / adhoc）。差分ゼロの行はノイズなので載せない。
  for (const c of session.drops) {
    if (c.source.kind === 'set-member') continue;
    const dN = deltaN(session, c);
    const dK = deltaK(c);
    if (dN === 0 && dK === 0) continue;
    const name = c.name || '(無名ドロップ)';
    if (c.source.kind === 'adhoc') {
      lines.push({ key: c.id, kind: 'new-line', name, deltaN: dN, deltaK: dK, selectable: true,
        note: `セットへ新規実績行を追加（N=${dN}, k=${dK}）` });
      continue;
    }
    const line = findLine(set, c.source.lineId);
    if (!line) {
      lines.push({ key: c.id, kind: 'orphan', name, deltaN: dN, deltaK: dK, selectable: true,
        note: '対応する行がセットに無い（削除済み）。取り込むと新規実績行として追加' });
    } else if (line.rateMode.kind === 'direct') {
      lines.push({ key: c.id, kind: 'direct-convert', name, deltaN: dN, deltaK: dK, selectable: true,
        note: `直接λ(${line.rateMode.lambda}) を事前分布（信頼度ふつう）にして実績+予測へ変換` });
    } else {
      lines.push({ key: c.id, kind: 'empirical-add', name, deltaN: dN, deltaK: dK, selectable: true,
        note: `実績に加算（N +${dN}, k +${dK}）` });
    }
  }

  // グループ（set-member カウンタを groupId ごとに集約。全メンバー一括が取り込み条件）。
  const byGroup = new Map<string, MeasureDropCounter[]>();
  for (const c of session.drops) {
    if (c.source.kind !== 'set-member') continue;
    const arr = byGroup.get(c.source.groupId) ?? [];
    arr.push(c);
    byGroup.set(c.source.groupId, arr);
  }
  for (const [groupId, counters] of byGroup) {
    const dNmax = Math.max(...counters.map((c) => deltaN(session, c)));
    const dKsum = counters.reduce((acc, c) => acc + deltaK(c), 0);
    if (dNmax === 0 && dKsum === 0) continue;
    const group = findGroup(set, groupId);
    if (!group) {
      lines.push({ key: `group:${groupId}`, kind: 'blocked', name: counters[0]?.name || '(グループ)',
        deltaN: dNmax, deltaK: dKsum, selectable: false, note: 'グループがセットから削除済みのため取り込み不可' });
      continue;
    }
    const covered = group.members.every((mem) =>
      counters.some((c) => c.source.kind === 'set-member' && c.source.memberId === mem.id));
    if (!covered) {
      lines.push({ key: `group:${groupId}`, kind: 'blocked', name: group.name || '(グループ)',
        deltaN: dNmax, deltaK: dKsum, selectable: false,
        note: '重みの尺度を揃えるため全メンバーのカウンタが必要（不足あり）。カウンタを追加してから取り込み' });
      continue;
    }
    lines.push({ key: `group:${groupId}`, kind: 'group-add', name: group.name || '(グループ)',
      deltaN: dNmax, deltaK: dKsum, selectable: true,
      note: `全メンバーを一括で実績化・加算（k 合計 +${dKsum}）` });
  }

  // t 更新提案（全期間の実測平均が最良の推定量。丸め後に差が無ければ提案しない）。
  const elapsedMs = effectiveElapsedMs(session, now);
  let timeProposal: ImportPlan['timeProposal'] = null;
  if (session.runs > 0 && elapsedMs > 0) {
    const measured = Math.max(1, Math.round(elapsedMs / 1000 / session.runs));
    if (measured !== set.time) timeProposal = { current: set.time, measured };
  }

  return { lines, timeProposal, empty: lines.filter((l) => l.selectable).length === 0 && timeProposal == null };
}

/** 実績系 rateMode への差分加算（direct は事前分布化して変換）。 */
function mergeRateMode(mode: RateMode | undefined, dN: number, dK: number): RateMode {
  if (!mode) return { kind: 'empirical', N: dN, k: dK };
  switch (mode.kind) {
    case 'direct':
      return { kind: 'empirical_prior', priorRate: Math.max(0, mode.lambda), strength: 'normal', N: dN, k: dK };
    case 'empirical':
      return { kind: 'empirical', N: mode.N + dN, k: mode.k + dK };
    case 'empirical_prior':
      return { ...mode, N: mode.N + dN, k: mode.k + dK };
  }
}

function advanceWatermark(session: MeasureSession, c: MeasureDropCounter): void {
  c.importedRuns = session.runs;
  c.importedCount = c.count;
}

function appendNewLine(set: FarmSet, c: MeasureDropCounter, dN: number, dK: number): void {
  const rateMode: RateMode = { kind: 'empirical', N: dN, k: dK };
  const line: DropLine = {
    id: newId(),
    name: c.name || '(無名ドロップ)',
    role: 'drop',
    itemId: c.itemId,
    unit: c.unit,
    value: c.value,
    count: resolveRate(rateMode).lambdaHat,
    rateMode,
  };
  set.dropLines.push(line);
  c.source = { kind: 'set-line', lineId: line.id };
}

/**
 * 取り込みの適用。set と session（ウォーターマーク・adhoc の source 付け替え）を破壊的に更新する。
 * 差分はプレビュー後の継続測定に備えて適用時点で再計算する。保存（saveSet/saveSession）は呼び出し側。
 */
export function applyImport(
  session: MeasureSession,
  set: FarmSet,
  choices: ImportChoices,
  now: number,
): ImportResult {
  const plan = planImport(session, set, now);
  const result: ImportResult = { updatedLines: 0, addedLines: 0, totalDeltaK: 0, maxDeltaN: 0, timeApplied: false };

  for (const planLine of plan.lines) {
    if (!planLine.selectable || !choices.selected.has(planLine.key)) continue;

    if (planLine.kind === 'group-add') {
      const groupId = planLine.key.slice('group:'.length);
      const group = findGroup(set, groupId);
      if (!group) continue;
      for (const mem of group.members) {
        const c = session.drops.find(
          (x) => x.source.kind === 'set-member' && x.source.groupId === groupId && x.source.memberId === mem.id,
        );
        if (!c) continue; // plan 時点で covered 検証済み。防御のみ
        const dN = deltaN(session, c);
        const dK = deltaK(c);
        mem.rateMode = mergeRateMode(mem.rateMode, dN, dK);
        result.totalDeltaK += dK;
        result.maxDeltaN = Math.max(result.maxDeltaN, dN);
        advanceWatermark(session, c);
      }
      result.updatedLines += 1;
      continue;
    }

    const c = session.drops.find((x) => x.id === planLine.key);
    if (!c) continue;
    const dN = deltaN(session, c);
    const dK = deltaK(c);
    result.totalDeltaK += dK;
    result.maxDeltaN = Math.max(result.maxDeltaN, dN);

    if (planLine.kind === 'new-line' || planLine.kind === 'orphan') {
      appendNewLine(set, c, dN, dK);
      result.addedLines += 1;
    } else {
      const line = c.source.kind === 'set-line' ? findLine(set, c.source.lineId) : null;
      if (!line) continue;
      line.rateMode = mergeRateMode(line.rateMode, dN, dK);
      // 期待値λ表示（count）を推定値に同期（calc-view の syncDropCount と同じ規約）。
      line.count = resolveRate(line.rateMode).lambdaHat;
      result.updatedLines += 1;
    }
    advanceWatermark(session, c);
  }

  if (choices.applyTime && plan.timeProposal) {
    set.time = plan.timeProposal.measured;
    result.timeApplied = true;
  }

  return result;
}
